import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  forwardRef,
  Inject,
  Input,
  OnInit, Optional,
  QueryList,
  ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

let id = 0;

@Component({
  selector: 'mcrai-input[type=checkbox]',
  template: `<label class="mcrai-input__label" [attr.for]="id">
  <input
    class="mcrai-input__checkbox-input"
    [id]="id"
    [checked]="checked"
    (click)="onClick()"
    [disabled]="isDisabled()"
    type="checkbox">
  <span
    class="mcrai-input__checkmark"
    [class.mcrai-input__checkmark--checked]="checked"
  ></span>
  <span class="mcrai-input__text">
    <ng-content></ng-content>
  </span>
</label>

`,
  styles: [`.mcrai-input--checkbox{margin-bottom:2px}.mcrai-input__label{display:flex;align-items:center;cursor:pointer;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}[disabled]>.mcrai-input__label{opacity:.4;cursor:default}.mcrai-input__checkbox-input{width:.1px;height:.1px;opacity:0;overflow:hidden;position:absolute;z-index:-1}.mcrai-input__checkmark{position:relative;top:0;left:0;height:20px;width:20px;border:1px solid;margin-right:10px}.mcrai-input__checkmark:after{content:'';position:absolute;display:none;left:11px;top:-2px;width:7px;height:15px;border-style:solid;border-width:0 2px 2px 0;-webkit-transform:rotate(45deg);transform:rotate(45deg)}.mcrai-input__checkmark--checked:after{display:block}`],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  host: {
    '[class.mcrai-input]': 'true',
    '[class.mcrai-input--checkbox]': 'true'
  }
})
export class McRAICheckbox implements OnInit {
  private _id = `mcrai-input--checkbox-${++id}`;
  get id() {
    return this._id;
  }

  private _disabled: undefined | null | boolean;
  @Input() set disabled(value: undefined | null | boolean) {
    this._disabled = value;
  }

  private _value: any;
  @Input() set value(value: any) {
    this._value = value;
  }
  get value(): any {
    return this._value;
  }

  private _checked;
  @Input() set checked(value: boolean) {
    this._checked = value;
    this._changeDetectorRef.markForCheck();
  }

  get checked(): boolean {
    return this._checked;
  }

  private _control: FormControl;
  @Input() set control(value: FormControl) {
    this._control = value;
  }
  get control(): FormControl {
    return this._control;
  }

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    @Optional() @Inject(forwardRef(() => McRAICheckboxGroup)) private _checkboxGroup: McRAICheckboxGroup
  ) { }

  ngOnInit() {
    this.updateChecked();
  }

  public onClick(): void {
    if (!this.isDisabled()) {
      this.checked = !this.checked;

      if (this._control) {
        this._control.markAsDirty();
        this._control.markAsTouched();
        this._control.setValue(this.checked);
      }

      if (this._checkboxGroup) {
        this._checkboxGroup.markAsDirty();
        this._checkboxGroup.markAsTouched();
        this._checkboxGroup.value = this._value;
      }
    }
  }

  public isDisabled(): boolean {
    return !this._disabled && typeof this._disabled !== 'undefined' || this._disabled === true;
  }

  public updateChecked(): void {
    if (this._checkboxGroup) {
      if (Array.isArray(this._checkboxGroup.value)) {
        this.checked = this._checkboxGroup.value.indexOf(this._value) !== -1;
      } else {
        this.checked = this._checkboxGroup.value === this._value;
      }
    } else if (this._control) {
      this.checked = this._control.value === this._value;
    }
  }
}

@Component({
  selector: 'mcrai-checkbox-group',
  template: `<ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  host: {
    '[class.mcrai-checkbox-group]': 'true'
  }
})
export class McRAICheckboxGroup implements OnInit, AfterViewInit {
  private _destroy: Subject<null> = new Subject<null>();

  @ContentChildren(McRAICheckbox, { descendants: true }) private _checkboxes: QueryList<McRAICheckbox>;

  _control: FormControl;
  @Input() set control(value: FormControl) {
    this._control = value;

    this._control.valueChanges
      .pipe(
        takeUntil(this._destroy)
      )
      .subscribe(() => this._checkboxes.forEach(checkbox => checkbox.updateChecked()));
  }

  set value(value: any) {
    if (!Array.isArray(this._control.value) || this._control.value.length === 0) {
      this._control.setValue([value]);
      return;
    }

    const filtered = this._control.value.filter(_value => _value !== value);

    if (this._control.value.length === filtered.length) {
      this._control.setValue(this._control.value.concat(value));
    } else {
      this._control.setValue(filtered);
    }
  }

  get value(): any {
    return this._control.value;
  }

  constructor() { }

  ngOnInit() { }

  ngAfterViewInit() { }

  ngOnDestroy() {
    this._destroy.next();
  }

  public markAsDirty(): void {
    this._control.markAsDirty();
  }

  public markAsTouched(): void {
    this._control.markAsTouched();
  }
}
